import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, delay },
});

const Section = ({ number, title, children }) => (
  <motion.div {...fade()} className="mb-12">
    <h2 className="font-display text-xl sm:text-2xl font-bold mb-4" style={{ color: "#1A1A1A" }}>
      {number}. {title}
    </h2>
    <div className="space-y-3 text-base leading-relaxed" style={{ color: "rgba(26,26,26,0.6)" }}>
      {children}
    </div>
  </motion.div>
);

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen relative overflow-hidden" style={{ backgroundColor: "#F5F5F0" }} data-testid="privacy-page">
      {/* ── HEADER ── */}
      <header
        className="fixed top-0 left-0 right-0 z-50 border-b"
        style={{ backgroundColor: "rgba(245,245,240,0.85)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", borderColor: "rgba(0,0,0,0.06)" }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="privacy-logo-link">
            <img
              src="https://customer-assets.emergentagent.com/job_2d4aff0d-e5b7-449b-860f-09baa683a8bc/artifacts/b3uq1zlx_Logo.png"
              alt="Prysh AI"
              className="w-9 h-9 object-contain"
            />
            <span className="font-display text-lg font-semibold" style={{ color: "#1A1A1A" }}>Prysh AI</span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium transition-colors"
            style={{ color: "rgba(26,26,26,0.5)" }}
            data-testid="privacy-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
        </div>
      </header>

      {/* ── HERO ── */}
      <section className="pt-32 pb-12 px-6 md:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <motion.h1
            {...fade(0.1)}
            className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05]"
            style={{ color: "#FF5A36" }}
            data-testid="privacy-headline"
          >
            Privacy Policy
          </motion.h1>
          <motion.p {...fade(0.2)} className="mt-4 text-sm" style={{ color: "rgba(26,26,26,0.4)" }}>
            Last Updated: 1 April 2026
          </motion.p>
        </div>
      </section>

      {/* ── INTRO ── */}
      <section className="px-6 md:px-12 pb-8">
        <motion.div {...fade()} className="max-w-3xl mx-auto">
          <p className="text-base leading-relaxed" style={{ color: "rgba(26,26,26,0.6)" }}>
            Prysh AI Labs ("Prysh", "we", "our", or "us") respects your privacy. This Privacy Policy explains how we collect, use, and protect your information when you use our AI-powered video creation platform.
          </p>
          <p className="text-base leading-relaxed mt-3" style={{ color: "rgba(26,26,26,0.6)" }}>
            By using Prysh, you agree to the terms of this Privacy Policy.
          </p>
        </motion.div>
      </section>

      {/* ── SECTIONS ── */}
      <section className="px-6 md:px-12 pb-20">
        <div className="max-w-3xl mx-auto">

          <Section number={1} title="Information We Collect">
            <p className="font-semibold" style={{ color: "#1A1A1A" }}>a) Information You Provide</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Name, email address, and account details</li>
              <li>Content you upload (text, images, logos, prompts)</li>
              <li>Payment information (processed via third-party providers)</li>
              <li>Communication with us (emails, support requests)</li>
            </ul>
            <p className="font-semibold mt-4" style={{ color: "#1A1A1A" }}>b) Automatically Collected Information</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Device and browser information</li>
              <li>IP address and location (approximate)</li>
              <li>Usage data (features used, videos generated, interactions)</li>
            </ul>
            <p className="font-semibold mt-4" style={{ color: "#1A1A1A" }}>c) AI-Generated Data</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Content generated by our platform based on your inputs</li>
              <li>Metadata related to generation (time, format, usage)</li>
            </ul>
          </Section>

          <Section number={2} title="How We Use Your Information">
            <p>We use your information to:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Provide and operate the platform</li>
              <li>Generate video content using AI</li>
              <li>Improve our models and product experience</li>
              <li>Personalize features and recommendations</li>
              <li>Communicate with you (updates, support, product info)</li>
              <li>Ensure platform safety and prevent misuse</li>
            </ul>
          </Section>

          <Section number={3} title="Use of AI & Data">
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Your inputs may be processed by AI systems to generate outputs</li>
              <li>We may use anonymized and aggregated data to improve our models</li>
              <li>We do not use your data to identify you personally in training</li>
            </ul>
            <p>You are responsible for ensuring you have the rights to the content you upload.</p>
          </Section>

          <Section number={4} title="Sharing of Information">
            <p>We do not sell your personal data.</p>
            <p>We may share your information with:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Trusted third-party service providers (hosting, payments, analytics, AI APIs)</li>
              <li>Legal authorities if required by law</li>
              <li>Business partners in case of a merger, acquisition, or asset sale</li>
            </ul>
          </Section>

          <Section number={5} title="Data Storage & Security">
            <ul className="list-disc pl-6 space-y-1.5">
              <li>We implement reasonable security measures to protect your data</li>
              <li>Data is stored on secure servers (may be located outside India)</li>
              <li>However, no system is completely secure</li>
            </ul>
          </Section>

          <Section number={6} title="Data Retention">
            <ul className="list-disc pl-6 space-y-1.5">
              <li>We retain your data as long as your account is active</li>
              <li>We may retain certain data for legal, security, or operational purposes</li>
            </ul>
            <p>You can request deletion of your data (see Section 8).</p>
          </Section>

          <Section number={7} title="Cookies & Tracking">
            <p>We may use cookies and similar technologies to:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Improve user experience</li>
              <li>Analyze platform usage</li>
              <li>Remember preferences</li>
            </ul>
            <p>You can control cookies through your browser settings.</p>
          </Section>

          <Section number={8} title="Your Rights">
            <p>You may:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Access or update your personal information</li>
              <li>Request deletion of your data</li>
              <li>Opt out of non-essential communications</li>
            </ul>
            <p>
              To exercise your rights, contact us at{" "}
              <a href="mailto:info@pryshai.com" className="font-medium hover:underline" style={{ color: "#FF5A36" }}>info@pryshai.com</a>
            </p>
          </Section>

          <Section number={9} title="Third-Party Services">
            <p>Our platform may include integrations with third-party tools.</p>
            <p>We are not responsible for their privacy practices. Please review their policies separately.</p>
          </Section>

          <Section number={10} title="Children's Privacy">
            <p>Prysh is not intended for users under the age of 18.</p>
            <p>We do not knowingly collect data from children.</p>
          </Section>

          <Section number={11} title="Changes to This Policy">
            <p>We may update this Privacy Policy from time to time.</p>
            <p>Continued use of the platform after updates means you accept the revised policy.</p>
          </Section>

          <Section number={12} title="Contact Us">
            <p>For any questions or concerns:</p>
            <p>
              <a href="mailto:info@pryshai.com" className="font-medium hover:underline" style={{ color: "#FF5A36" }}>info@pryshai.com</a>
            </p>
          </Section>

        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="relative z-10 border-t py-12 px-6 md:px-12" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-sm mb-4" style={{ color: "rgba(26,26,26,0.5)" }}>
            For questions, contact us at:{" "}
            <a href="mailto:info@pryshai.com" className="font-medium hover:underline" style={{ color: "#FF5A36" }} data-testid="privacy-contact-email">
              info@pryshai.com
            </a>
          </p>
          <p className="text-xs" style={{ color: "rgba(26,26,26,0.25)" }}>
            &copy; {new Date().getFullYear()} Prysh AI Labs. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
